import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CollegeRequests from './CollegeRequests';
import AdminCustomerApproval from './AdminCustomerApproval'; 
import './AdminUserManagement.css';

import {
  UserPlus,
  Trash2,
  Filter,
  Building2,
  UserCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ClipboardCheck,
  Power,
  Users,
  UserCheck,
  Eye,      // <--- NEW: Eye Icon
  EyeOff    // <--- NEW: EyeOff Icon
} from 'lucide-react';

const ITEMS_PER_PAGE = 15;

const AdminUserManagement = () => {
  const [view, setView] = useState('list'); // 'list', 'create', 'requests', 'customer-requests'
  const [filterRole, setFilterRole] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- NOTIFICATION COUNTS ---
  const [pendingCollegeCount, setPendingCollegeCount] = useState(0);
  const [pendingCustomerCount, setPendingCustomerCount] = useState(0);
  
  const navigate = useNavigate();

  const [role, setRole] = useState('Recruiter'); 
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // 🚨 [NEW] State for toggling password visibility 🚨
  const [showPassword, setShowPassword] = useState(false);

  // --- FETCH USERS ---
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`, {
        headers: { 'Authorization': token }
      });
      setUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        alert("Session expired. Please login again.");
        localStorage.removeItem('adminToken');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // --- FETCH PENDING COUNTS ---
  const fetchPendingCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch College Requests
      const resColleges = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/college-requests`, {
        headers: { 'Authorization': token }
      });
      setPendingCollegeCount(resColleges.data.length);

      // Fetch Customer Requests
      const resCustomers = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/pending-customers`, {
        headers: { 'Authorization': token }
      });
      setPendingCustomerCount(resCustomers.data.length);

    } catch (error) {
      console.error("Failed to fetch pending requests counts", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendingCounts();
  }, [fetchUsers, fetchPendingCounts]);

  useEffect(() => {
    if (view === 'list') fetchPendingCounts(); // Refresh counts when going back to list
  }, [view, fetchPendingCounts]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole]);

  // --- CREATE USER ---
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (role === 'Recruiter') {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/recruiter/register`, {
          username: name, email, password
        });
      }
      alert(`New ${role} created successfully!`);
      setName(''); setEmail(''); setPassword(''); setShowPassword(false); // reset visibility
      setView('list');
      fetchUsers();
    } catch (error) {
      console.error("Create User Error:", error);
      alert(error.response?.data?.message || `Failed to create ${role}`);
    }
  };

  // --- TOGGLE STATUS ---
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newStatus = !currentStatus;
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${id}/status`,
        { isActive: newStatus },
        { headers: { 'Authorization': token } }
      );
      setUsers(users.map(user =>
        (user._id === id || user.id === id) ? { ...user, isActive: newStatus } : user
      ));
    } catch (error) {
      console.error("Toggle Status Error:", error);
      alert("Failed to update user status.");
    }
  };

  // --- DELETE USER ---
  const handleDeleteUser = async (id, userRole) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${id}`, {
          headers: { 'Authorization': token }
        });
        setUsers(users.filter(user => (user._id !== id && user.id !== id)));
      } catch (error) {
        console.error("Delete Error:", error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          alert("Session expired. Please login again.");
          navigate('/');
        } else {
          alert("Failed to delete user. Check console.");
        }
      }
    }
  };

  // --- FILTER & PAGINATE ---
  const filteredUsers = users.filter(user => {
    if (filterRole === 'All') return true;
    return user.role === filterRole;
  });

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ===== RENDER USER LIST =====
  const renderUserList = () => (
    <div className="aum-panel">

      {/* ── Stats Row ── */}
      <div className="aum-stats-row">
        <div className="aum-stat-card indigo">
          <div className="aum-stat-icon"><Users size={18} /></div>
          <div className="aum-stat-body">
            <span className="aum-stat-num">{users.length}</span>
            <span className="aum-stat-lbl">Total Users</span>
          </div>
        </div>
        <div className="aum-stat-card sapphire">
          <div className="aum-stat-icon"><Building2 size={18} /></div>
          <div className="aum-stat-body">
            <span className="aum-stat-num">{users.filter(u => u.role === 'Customer').length}</span>
            <span className="aum-stat-lbl">Employers</span>
          </div>
        </div>
        <div className="aum-stat-card gold">
          <div className="aum-stat-icon"><UserCircle2 size={18} /></div>
          <div className="aum-stat-body">
            <span className="aum-stat-num">{users.filter(u => u.role === 'Recruiter').length}</span>
            <span className="aum-stat-lbl">Recruiters</span>
          </div>
        </div>
        <div className="aum-stat-card emerald">
          <div className="aum-stat-icon"><Power size={18} /></div>
          <div className="aum-stat-body">
            <span className="aum-stat-num">{users.filter(u => u.isActive).length}</span>
            <span className="aum-stat-lbl">Active</span>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="aum-toolbar">
        <p className="aum-showing">
          Showing <strong>{paginatedUsers.length}</strong> of{' '}
          <strong>{filteredUsers.length}</strong> users
        </p>
        <div className="aum-filter-group">
          <span className="aum-filter-icon"><Filter size={13} /></span>
          {['All', 'Customer', 'Recruiter'].map(r => (
            <button
              key={r}
              type="button"
              className={`aum-filter-btn ${filterRole === r ? 'active' : ''}`}
              onClick={() => setFilterRole(r)}
            >
              {r === 'All' ? 'All Users' : `${r}s`}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="aum-loading">
          <Loader2 className="aum-spin" size={30} />
          <span>Loading users…</span>
        </div>
      ) : (
        <>
          <div className="aum-table-wrap">
            <table className="aum-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Name / Company</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Initial Password</th>
                  <th>Status</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length > 0 ? paginatedUsers.map((user, idx) => (
                  <tr key={user._id || user.id}>
                    {/* # */}
                    <td className="aum-td-num">
                      {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                    </td>

                    {/* Name */}
                    <td>
                      <div className="aum-name-cell">
                        <div className="aum-avatar">
                          {user.role === 'Customer'
                            ? <Building2 size={15} />
                            : <UserCircle2 size={15} />}
                        </div>
                        <span className="aum-uname">
                          {user.fullName || user.username || user.name}
                        </span>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="aum-td-email">{user.email}</td>

                    {/* Role */}
                    <td>
                      <span className={`aum-role-badge ${user.role?.toLowerCase() || 'unknown'}`}>
                        {user.role || 'User'}
                      </span>
                    </td>

                    {/* Initial Password */}
                    <td>
                      <span className="aum-pwd">
                        {user.initialPassword || 'N/A'}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <button
                        type="button"
                        className={`aum-status-btn ${user.isActive ? 'on' : 'off'}`}
                        onClick={() => handleToggleStatus(user._id || user.id, user.isActive)}
                      >
                        <Power size={11} />
                        {user.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="aum-delete-btn"
                        onClick={() => handleDeleteUser(user._id || user.id, user.role)}
                      >
                        <Trash2 size={13} />
                        Delete
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="aum-empty">
                      <Users size={36} />
                      <p>No {filterRole === 'All' ? 'users' : `${filterRole}s`} found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="aum-pagination">
              <span className="aum-page-info">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              <div className="aum-page-controls">
                <button
                  className="aum-page-btn nav"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={15} />
                </button>
                {getPageNumbers().map(p => (
                  <button
                    key={p}
                    className={`aum-page-btn ${currentPage === p ? 'active' : ''}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="aum-page-btn nav"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ===== RENDER CREATE FORM =====
  const renderCreateForm = () => (
    <div className="aum-form-card">
      <div className="aum-form-card-header">
        <div className="aum-form-card-icon"><UserPlus size={20} /></div>
        <div>
          <h3>Create New User Account</h3>
          <p>Fill in the details below to add a new Recruiter to the system</p>
        </div>
      </div>
      <form onSubmit={handleCreateUser} className="aum-form-body">
        <div className="aum-form-grid">
          <div className="aum-field">
            <label className="aum-field-label">Select Role</label>
            <select value={role} onChange={e => setRole(e.target.value)} className="aum-select">
              <option value="Recruiter">Recruiter</option>
            </select>
          </div>
          <div className="aum-field">
            <label className="aum-field-label">Username</label>
            <input
              type="text"
              placeholder="Enter username"
              value={name}
              onChange={e => setName(e.target.value)}
              className="aum-input"
              required
            />
          </div>
          <div className="aum-field">
            <label className="aum-field-label">Email Address</label>
            <input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="aum-input"
              required
            />
          </div>
          <div className="aum-field">
            <label className="aum-field-label">Password</label>
            {/* 🚨 THE FIX: Password input wrapper with the Eye icon toggle 🚨 */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Assign a password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="aum-input"
                required
                style={{ width: '100%', paddingRight: '40px' }} // Added padding so text doesn't hide behind icon
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  padding: 0
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
        <div className="aum-form-footer">
          <button type="button" className="aum-btn-ghost" onClick={() => { setView('list'); setShowPassword(false); }}>
            <ChevronLeft size={15} /> Cancel
          </button>
          <button type="submit" className="aum-btn-gold">
            <UserPlus size={15} /> Create {role}
          </button>
        </div>
      </form>
    </div>
  );

  // ===== MAIN RETURN =====
  const pageTitle = view === 'list'
    ? 'User Management'
    : view === 'requests'
    ? 'College Requests'
    : view === 'customer-requests'
    ? 'Customer Requests'
    : 'Create New User';

  const pageSubtitle = view === 'list'
    ? 'Manage and monitor all admin-created accounts'
    : view === 'requests'
    ? 'Review and approve pending college registrations'
    : view === 'customer-requests'
    ? 'Review and approve pending corporate employer accounts'
    : 'Add a new user account to the system';

  return (
    <div className="aum-root">

      {/* ── Top Header ── */}
      <div className="aum-header">
        <div className="aum-header-left">
          <h1 className="aum-title">{pageTitle}</h1>
          <p className="aum-subtitle">{pageSubtitle}</p>
        </div>
        <div className="aum-header-actions">
          
          {view === 'list' && (
            <button
              type="button"
              className="aum-hdr-btn ghost"
              onClick={() => setView('customer-requests')}
              style={{ position: 'relative' }}
            >
              <UserCheck size={15} />
              Employer Requests
              {pendingCustomerCount > 0 && (
                <span className="aum-badge-dot">{pendingCustomerCount}</span>
              )}
            </button>
          )}

          {view === 'list' && (
            <button
              type="button"
              className="aum-hdr-btn ghost"
              onClick={() => setView('requests')}
              style={{ position: 'relative' }}
            >
              <ClipboardCheck size={15} />
              College Requests
              {pendingCollegeCount > 0 && (
                <span className="aum-badge-dot">{pendingCollegeCount}</span>
              )}
            </button>
          )}

          {view === 'list' && (
            <button
              type="button"
              className="aum-hdr-btn gold"
              onClick={() => setView('create')}
            >
              <UserPlus size={15} /> Create New User
            </button>
          )}

          {(view === 'create' || view === 'requests' || view === 'customer-requests') && (
            <button
              type="button"
              className="aum-hdr-btn ghost"
              onClick={() => {
                setView('list');
                setShowPassword(false); // Reset password visibility if they click back
                fetchPendingCounts(); 
              }}
            >
              <ChevronLeft size={15} /> Back to Users
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="aum-body">
        {view === 'list'              && renderUserList()}
        {view === 'create'            && renderCreateForm()}
        {view === 'requests'          && <CollegeRequests />}
        {view === 'customer-requests' && <AdminCustomerApproval />} 
      </div>

    </div>
  );
};

export default AdminUserManagement;
